import { useDisplayModeData } from '@/hooks/use-display-mode-data';
import { cn } from '@/lib/utils';
import {
  ColumnFilterId,
  useSetColumnFilter,
} from '@/states/column-filters.state';
import { displayModeState } from '@/states/display-mode.state';
import { scrollToTableColumnIdState } from '@/states/scroll-to-table-column.state';
import { ColumnAccessor } from '@/types/columns';
import { DisplayMode } from '@/types/display-mode';
import {
  ButtonDropdown,
  ButtonDropdownButton,
  ButtonDropdownMenuContent,
  ButtonDropdownMenuItem,
  ButtonDropdownMenuTrigger,
} from '@appchoose/button-dropdown';
import { useAtom, useSetAtom } from 'jotai';
import { ChevronDown } from 'lucide-react';

import { useUser } from '../../hooks/use-user';
import { TabConfig, TabIcon } from './displaymode-buttons';
import { StreamingCount } from './streaming-count';

function DisplayModeButtonDropdown({ tabs }: { tabs: TabConfig[] }) {
  const [displayMode, setDisplayMode] = useAtom(displayModeState);
  const user = useUser();

  const { getDisplayModeLabel, getDisplayModeCount } = useDisplayModeData();

  const { setColumnFilter } = useSetColumnFilter();
  const setScrollToColumnId = useSetAtom(scrollToTableColumnIdState);

  return (
    <div className="flex items-center">
      <ButtonDropdown
        className="inline-flex h-10 items-center divide-x-0 rounded-md bg-muted p-1"
        value={displayMode}
        onValueChange={(value) => {
          setColumnFilter(ColumnFilterId.__displayMode, value as DisplayMode);
          setDisplayMode(value as DisplayMode);
          setScrollToColumnId(ColumnAccessor.isBestSeller);
        }}
      >
        <ButtonDropdownMenuTrigger asChild>
          <ButtonDropdownButton
            className={cn(
              'inline-flex h-full min-h-0 shrink-0 cursor-pointer items-center gap-2 rounded-sm border-0 bg-background px-3 py-1.5 text-sm font-medium text-foreground shadow-sm outline-none transition-all',
              'hover:bg-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              'disabled:pointer-events-none disabled:opacity-50'
            )}
          >
            <TabIcon mode={displayMode} role={user?.role} />
            <span className="whitespace-nowrap">
              {getDisplayModeLabel(displayMode)}
            </span>
            <span className="font-normal text-muted-foreground">
              <StreamingCount count={getDisplayModeCount(displayMode)} />
            </span>
            <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
          </ButtonDropdownButton>
        </ButtonDropdownMenuTrigger>
        <ButtonDropdownMenuContent
          className={cn(
            'z-50 min-w-[8rem] overflow-hidden rounded-md border bg-popover p-1 text-popover-foreground shadow-md',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2'
          )}
        >
          {tabs.map(({ mode, dataUserGuide }) => (
            <ButtonDropdownMenuItem
              key={mode}
              value={mode}
              data-user-guide={dataUserGuide}
              className={cn(
                'flex cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
                'focus:bg-accent focus:text-accent-foreground data-[state=selected]:bg-accent/50'
              )}
            >
              <span className="flex min-w-0 items-center gap-2">
                <TabIcon mode={mode} role={user?.role} />
                <span className="truncate">{getDisplayModeLabel(mode)}</span>
              </span>
              <span className="shrink-0 font-normal text-muted-foreground">
                <StreamingCount count={getDisplayModeCount(mode)} />
              </span>
            </ButtonDropdownMenuItem>
          ))}
        </ButtonDropdownMenuContent>
      </ButtonDropdown>
    </div>
  );
}

export default DisplayModeButtonDropdown;
